import React, { useState, useEffect } from 'react';

interface CoverageData {
  project: string;
  totalLines: number;
  coveredLines: number;
  coveragePercentage: number;
  uncoveredLines: number;
  lastUpdated: string;
  trend: 'up' | 'down' | 'stable';
  files: {
    name: string;
    coverage: number;
    lines: number;
    uncovered: number;
  }[];
}

const CoverageComponent: React.FC = () => {
  const [coverageData, setCoverageData] = useState<CoverageData[]>([]);
  const [isLoading, setIsLoading] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  useEffect(() => {
    fetchCoverageData();
  }, []);

  const fetchCoverageData = async () => {
    setIsLoading(true);
    try {
      const mockCoverage: CoverageData[] = [
        {
          project: 'admin-dashboard',
          totalLines: 2500,
          coveredLines: 2250,
          coveragePercentage: 90,
          uncoveredLines: 250,
          lastUpdated: new Date(Date.now() - 3600000).toISOString(),
          trend: 'up',
          files: [
            { name: 'AdminDashboard.tsx', coverage: 95, lines: 500, uncovered: 25 },
            { name: 'SuperAdminDashboard.tsx', coverage: 88, lines: 800, uncovered: 96 },
            { name: 'ChillSetterDashboard.tsx', coverage: 92, lines: 400, uncovered: 32 }
          ]
        },
        {
          project: 'landing-page',
          totalLines: 1800,
          coveredLines: 1620,
          coveragePercentage: 90,
          uncoveredLines: 180,
          lastUpdated: new Date(Date.now() - 7200000).toISOString(),
          trend: 'stable',
          files: [
            { name: 'App.tsx', coverage: 94, lines: 300, uncovered: 18 },
            { name: 'AffiliateRegister.tsx', coverage: 87, lines: 250, uncovered: 33 },
            { name: 'JoinReferral.tsx', coverage: 89, lines: 200, uncovered: 22 }
          ]
        },
        {
          project: 'backend-api',
          totalLines: 3200,
          coveredLines: 2880,
          coveragePercentage: 90,
          uncoveredLines: 320,
          lastUpdated: new Date(Date.now() - 1800000).toISOString(),
          trend: 'down',
          files: [
            { name: 'admin.js', coverage: 92, lines: 600, uncovered: 48 },
            { name: 'affiliate.js', coverage: 88, lines: 400, uncovered: 48 },
            { name: 'qr-tracking.js', coverage: 85, lines: 300, uncovered: 45 }
          ]
        }
      ];
      setCoverageData(mockCoverage);
    } catch (error) {
      console.error('Error fetching coverage data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '📈';
      case 'down': return '📉';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  const getCoverageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-scarf-green';
    if (percentage >= 80) return 'text-brand-blue-light';
    if (percentage >= 70) return 'text-brand-red';
    return 'text-brand-red';
  };

  const getCoverageBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-scarf-green';
    if (percentage >= 80) return 'bg-brand-blue-light';
    if (percentage >= 70) return 'bg-brand-red';
    return 'bg-brand-red';
  };

  return (
    <div className="space-y-6">
      {/* Coverage Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        {coverageData.map((project) => (
          <div key={project.project} className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-title font-bold text-deep-navy">{project.project}</h3>
              <span className="text-2xl">{getTrendIcon(project.trend)}</span>
            </div>

            <div className="text-center mb-4">
              <div className={`text-4xl font-bold ${getCoverageColor(project.coveragePercentage)}`}>
                {project.coveragePercentage}%
              </div>
              <p className="text-gray-500 text-sm">
                {project.coveredLines} / {project.totalLines} lines
              </p>
            </div>

            <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
              <div
                className={`h-2 rounded-full ${getCoverageBarColor(project.coveragePercentage)}`}
                style={{ width: `${project.coveragePercentage}%` }}
              ></div>
            </div>

            <div className="text-center">
              <p className="text-gray-500 text-sm">
                Last updated: {new Date(project.lastUpdated).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Coverage Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={fetchCoverageData}
            className="bg-brand-red hover:bg-brand-red/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Refresh Coverage
          </button>
          <button
            onClick={() => console.log('Generate coverage report')}
            className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Generate Report
          </button>
          <button
            onClick={() => console.log('Export coverage data')}
            className="bg-scarf-green hover:bg-scarf-green/90 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Export Data
          </button>
        </div>
      </div>

      {/* Detailed Coverage by File */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">File-Level Coverage</h3>
        <div className="space-y-4">
          {coverageData.map((project) => (
            <div key={project.project} className="border-b border-gray-100 pb-4 last:border-b-0">
              <h4 className="text-lg font-bold text-deep-navy mb-3">{project.project}</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-gray-800">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-4 text-deep-navy font-bold">File</th>
                      <th className="text-left py-2 px-4 text-deep-navy font-bold">Coverage</th>
                      <th className="text-left py-2 px-4 text-deep-navy font-bold">Lines</th>
                      <th className="text-left py-2 px-4 text-deep-navy font-bold">Uncovered</th>
                      <th className="text-left py-2 px-4 text-deep-navy font-bold">Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.files.map((file) => (
                      <tr key={file.name} className="border-b border-gray-50">
                        <td className="py-2 px-4 font-mono text-sm">{file.name}</td>
                        <td className="py-2 px-4">
                          <span className={`font-bold ${getCoverageColor(file.coverage)}`}>
                            {file.coverage}%
                          </span>
                        </td>
                        <td className="py-2 px-4">{file.lines}</td>
                        <td className="py-2 px-4">{file.uncovered}</td>
                        <td className="py-2 px-4">
                          <div className="w-24 bg-gray-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${getCoverageBarColor(file.coverage)}`}
                              style={{ width: `${file.coverage}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coverage Trends */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Coverage Trends</h3>
        <div className="grid md:grid-cols-3 gap-6">
          {coverageData.map((project) => (
            <div key={project.project} className="text-center">
              <h4 className="text-deep-navy font-bold mb-2">{project.project}</h4>
              <div className="text-3xl mb-2">{getTrendIcon(project.trend)}</div>
              <p className="text-gray-500 text-sm">
                {project.trend === 'up' ? 'Coverage increasing' :
                 project.trend === 'down' ? 'Coverage decreasing' :
                 'Coverage stable'}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoverageComponent;
